import '../../style.css';

import { Map as OlMap, View } from 'ol';
import { useGeographic } from 'ol/proj';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import GeoJSON from 'ol/format/GeoJSON';
import { Icon, Style, Text } from 'ol/style';
import Select, { SelectEvent } from 'ol/interaction/Select';
import { click } from 'ol/events/condition';
import { Extent } from 'ol/extent';

import { Application, getApplicationInstance } from '../../core/app';
import CragModel from '../../core/crag_model';

import { MapFactory } from '../interface'

import { Mountain, Stone } from 'lucide';

import * as Icons from './icons';
import * as Geo from './geometry';
import * as Zoom from './zoom';
import { FeatureLike } from 'ol/Feature';

enum Type {
    Crag = "Crag",
    Boulder = "Boulder"
}

const cragIcon = new Icon({
    src: `data:image/svg+xml;utf8,${Icons.encircle(Mountain)}`,
    anchor: [0.5, 1]
});


const cragStyle = new Style({
    image: new Icon({
        src: `data:image/svg+xml;utf8,${Icons.encircle(Mountain)}`,
        anchor: [0.5, 1]
    })
});


const boulderIcon = new Icon({
    src: `data:image/svg+xml;utf8,${Icons.encircle(Stone)}`,
    anchor: [0.5, 1]
})

const boulderSelectIcon = new Icon({
    src: `data:image/svg+xml;utf8,${Icons.encircle(Stone, { fill: 'gold' })}`,
    anchor: [0.5, 1]
})


let initialGeometry: Geo.Geometry = new Geo.Geometry(
    [0, 0],
    1
);
let preSelectGeometry: Geo.Geometry | null = null;

let boulderLayer: VectorLayer | null;

function getCragStyle(feature: FeatureLike, app: Application) {
    if (feature === app.crag.value?.feature) {
        return null;
    }
    else {
        return new Style({ image: cragIcon, text: new Text({ text: feature.get('name') }) });
    }
}

function getBoulderStyle(feature: FeatureLike, app: Application) {
    const icon: Icon = feature === app.boulder.value?.feature ? boulderSelectIcon : boulderIcon;
    return new Style({ image: icon, text: new Text({ text: feature.get('name') }) })
}

function initialFit(extent: Extent | null, map: OlMap) {
    if (extent) {
        initialGeometry = Geo.Geometry.fromExtent(extent, map);
        map.getView().fit(extent, { padding: [50, 50, 50, 50] });
    }
}

function removeBoulderLayer(map: OlMap) {
    if (boulderLayer) {
        map.removeLayer(boulderLayer);
        boulderLayer = null;
    }
}

function onFeatureSelect(event: SelectEvent, map: OlMap) {
    const app = getApplicationInstance();
    const feature = event.selected[0];
    if (app.crag.value?.feature === feature) {
        return;
    }
    if (!feature) {
        app.crag = null;
        removeBoulderLayer(map);
        if (preSelectGeometry) {
            const view = map.getView();
            view.animate({ center: preSelectGeometry.center, resolution: preSelectGeometry.resolution });
        }
        preSelectGeometry = null;
    }
    if (feature) {
        let featureType = Type.Boulder;
        let newType = feature.get('type');
        if (newType) {
            featureType = newType;
        }
        switch (featureType) {
            case Type.Boulder:
                app.boulder = app.crag.value?.boulders.find(boulder => boulder.feature === feature) || null;
                break;
            case Type.Crag:
                removeBoulderLayer(map);
                app.crag = new CragModel(feature);
                const source = new VectorSource({ features: app.crag.value.boulders.map(boulder => boulder.feature) });
                boulderLayer = new VectorLayer({
                    source: source,
                    style: (feature) => getBoulderStyle(feature, app)
                });
                map.addLayer(boulderLayer);

                const view = map.getView();
                preSelectGeometry = {
                    center: view.getCenter() || initialGeometry.center,
                    resolution: view.getResolution() || initialGeometry.resolution
                };
                Zoom.start(source.getExtent(), map);
                break;
        }
    }
}

function createMap(elementName: string) {
    useGeographic();

    const source = new VectorSource({
        url: 'places/places.geojson',
        format: new GeoJSON()
    });

    let cragLayer = new VectorLayer({
        source: source,
        style: (feature) => getCragStyle(feature, app)
    });

    let map = new OlMap({
        target: elementName,
        layers: [
            new TileLayer({
                source: new OSM()
            }),
            cragLayer
        ],
        view: new View({
            center: initialGeometry.center,
            resolution: initialGeometry.resolution
        })
    });

    const select = new Select({
        condition: click, style: null
    });

    map.addInteraction(select);

    source.on('featuresloadend', () => {
        initialFit(source.getExtent(), map);
    });

    select.on('select', (event) => { onFeatureSelect(event, map); });

    map.on('change:size', () => {
        Zoom.update(map);
    });

    const app = getApplicationInstance();
    app.line.subscribe(() => {
        boulderLayer?.changed();
    });
    app.boulder.subscribe(() => {
        boulderLayer?.changed();
    });
}

const module = {
    createMap
} satisfies MapFactory;
export default module;
